import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';

import { LockResetTwoTone } from '@mui/icons-material';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import UnfoldMoreTwoToneIcon from '@mui/icons-material/UnfoldMoreTwoTone';
import {
  Avatar,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography,
  alpha,
  styled
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LogoutConfirmation from 'src/components/LogoutConfirmationDialog';
import ResetPasswordAdmin from 'src/components/ResetPasswordDialog';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
    padding: ${theme.spacing(1)};
    background-color: ${alpha(theme.colors.alpha.black[100], 0.08)};

    .MuiButton-label {
      justify-content: flex-start;
    }

    &:hover {
      background-color: ${alpha(theme.colors.alpha.black[100], 0.12)};
    }
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
    background: ${theme.colors.alpha.black[5]};
    padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
    text-align: left;
    padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
    font-weight: ${theme.typography.fontWeightBold};
    color: ${theme.sidebar.menuItemColor};
    display: block;

    &.popoverTypo {
      color: ${theme.palette.secondary.main};
    }
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
    color: ${alpha(theme.sidebar.menuItemColor, 0.6)};

    &.popoverTypo {
      color: ${theme.palette.secondary.light};
    }
`
);

function SidebarTopSection() {
  const { t }: { t: any } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { user, logout } = useAuth();
  const avatar = user.gimageUrl
    ? user.gimageUrl
    : user.fbImageUrl
    ? user.fbImageUrl
    : '/static/images/avatars/avatar-1.jpg';

  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const categoryTypeId = user?.categoryTypeId;
  const [isMounted, setIsMounted] = useState(false);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  const handleCloseConfirmation = (): void => {
    if (isMounted) {
      setConfirmationOpen(false);
      setOpen(false);
    }
  };

  const handleConfirmLogout = async () => {
    try {
      // Perform the logout logic
      await logout();
      if (categoryTypeId === 'TCV') {
        navigate('/account/vendor/login');
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error(err);
    } finally {
      // Close the confirmation modal
      handleCloseConfirmation();
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLogout = async (): Promise<void> => {
    try {
      handleClose();
      await logout();
      navigate('/');
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <UserBoxButton fullWidth color="secondary" ref={ref} onClick={handleOpen}>
        <Avatar variant="rounded" alt={user.name} src={avatar ? avatar : ''} />
        <Box
          display="flex"
          flex={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <UserBoxText>
            <UserBoxLabel variant="body1">{user.name}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {user.roleIdName}
            </UserBoxDescription>
          </UserBoxText>
          <UnfoldMoreTwoToneIcon
            fontSize="small"
            sx={{
              ml: 1
            }}
          />
        </Box>
      </UserBoxButton>
      <Popover
        disableScrollLock
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center'
        }}
        BackdropProps={{ invisible: true }}
      >
        <MenuUserBox
          sx={{
            minWidth: 210
          }}
          display="flex"
        >
          <Avatar
            variant="rounded"
            alt={user.name}
            src={avatar ? avatar : ''}
          />
          <UserBoxText>
            <UserBoxLabel className="popoverTypo" variant="body1">
              {user.name}
            </UserBoxLabel>
            <UserBoxDescription className="popoverTypo" variant="body2">
              {user.roleIdName}
            </UserBoxDescription>
          </UserBoxText>
        </MenuUserBox>
        <Divider
          sx={{
            mb: 0
          }}
        />
        <List
          sx={{
            p: 1
          }}
          component="nav"
        >
          {/* <ListItem
            onClick={() => {
              handleClose();
            }}
            button
            to={`/${
              location.pathname.split('/')[1]
            }/dashboard/management/user-management/users/${user.id}`}
            component={NavLink}
          >
            <AccountBoxTwoToneIcon fontSize="small" />
            <ListItemText primary={t('Profile')} />
          </ListItem> */}
          <ListItem
            button
            onClick={() => {
              handleOpenDialog();
            }}
          >
            <LockResetTwoTone fontSize="small" />
            <ListItemText primary={t('Reset Password')} />
          </ListItem>
        </List>
        <Divider />
        <Box m={1}>
          <Button
            sx={{
              display: 'flex',
              flexDirection: 'row',
              padding: '9px 23px',
              gap: '4px',
              justifyContent: 'start',
              alignItems: 'center'
            }}
            color="primary"
            fullWidth
            onClick={handleLogout}
          >
            <LockOpenTwoToneIcon
            // sx={{
            //   mr: 1
            // }}
            />
            <span>{t('Sign Out')}</span>
          </Button>
          <LogoutConfirmation
            open={isConfirmationOpen}
            onClose={handleCloseConfirmation}
            onConfirm={handleConfirmLogout}
          />
        </Box>
      </Popover>
      <ResetPasswordAdmin
        open={openDialog}
        handleClose={handleCloseDialog}
        userId={user?.id}
      />
    </>
  );
}

export default SidebarTopSection;
